import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

/**
 * AnimatedCounter — counts up from 0 to a target value when in view.
 * Accepts a string value (like "20+", "99.9%", "2,000+") and animates the numeric portion.
 */
const AnimatedCounter = ({ value, label, duration = 1.6, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [display, setDisplay] = useState(0);

  // Parse number + suffix from value string
  const stringVal = String(value);
  const match = stringVal.match(/(\d[\d,.]*)/);
  const numStr = match ? match[1] : "0";
  const numeric = Number(numStr.replace(/,/g, ""));
  const isFloat = numStr.includes(".");
  const decimals = isFloat ? (numStr.split(".")[1] || "").length : 0;

  const prefix = match ? stringVal.slice(0, match.index) : "";
  const suffix = match ? stringVal.slice(match.index + match[0].length) : "";

  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (ts) => {
      if (start === undefined) start = ts;
      const progress = Math.min((ts - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDisplay(numeric * eased);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, numeric, duration]);

  const formatted = decimals
    ? display.toFixed(decimals)
    : Math.round(display).toLocaleString();

  return (
    <div ref={ref} className={className}>
      <p className="stat-number text-3xl sm:text-4xl lg:text-5xl">
        {prefix}
        {formatted}
        {suffix}
      </p>
      {label && (
        <p className="text-xs sm:text-sm text-[var(--text-secondary)] mt-1.5 font-medium tracking-wide">
          {label}
        </p>
      )}
    </div>
  );
};

export default AnimatedCounter;
